import React, { useCallback, useEffect, useMemo, useState } from "react";
import "./CrmDashboard.css";

const API = "/api";

const SERVICE_OPTIONS = [
  "Instant PAN Card",
  "PAN Card Retailer",
  "UDYAM Certificate",
  "Shop Act",
  "Food License",
  "Other",
];

const STATUS_OPTIONS = [
  "NEW",
  "CONTACTED",
  "QUALIFIED",
  "CONVERTED",
  "LOST",
];

function getToken() {
  return localStorage.getItem("potech_token") || "";
}

function getUser() {
  try {
    return JSON.parse(localStorage.getItem("potech_user") || "{}");
  } catch {
    return {};
  }
}

function isToday(value) {
  if (!value) return false;
  const date = new Date(value);
  const now = new Date();

  return (
    date.getFullYear() === now.getFullYear() &&
    date.getMonth() === now.getMonth() &&
    date.getDate() === now.getDate()
  );
}

function isOverdue(value) {
  return value && new Date(value).getTime() < Date.now() && !isToday(value);
}

function formatDate(value) {
  if (!value) return "Not scheduled";
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? value
    : date.toLocaleString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
}

export default function CrmDashboard({ onLogout }) {
  const [leads, setLeads] = useState([]);
  const [view, setView] = useState("overview");
  const [statusFilter, setStatusFilter] = useState("ALL");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");

  const [form, setForm] = useState({
    fullName: "",
    phone: "",
    email: "",
    companyName: "",
    source: "MANUAL",
    notes: "",
    nextFollowUp: "",
  });

  const user = useMemo(() => getUser(), []);
  const role = String(user.role || "").toUpperCase();
  const isPortalAdmin =
    role === "SUPER_ADMIN" || role === "PORTAL_ADMIN";

  const request = useCallback(async (path, options = {}) => {
    const response = await fetch(`${API}${path}`, {
      ...options,
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${getToken()}`,
        ...(options.headers || {}),
      },
    });

    if (!response.ok) {
      const message = await response.text();
      throw new Error(message || `Request failed (${response.status})`);
    }

    if (response.status === 204) return null;
    return response.json();
  }, []);

  const loadLeads = useCallback(async () => {
    setLoading(true);
    setError("");

    try {
      const result = await request("/leads");
      setLeads(Array.isArray(result) ? result : result?.content || []);
    } catch (e) {
      setError(e.message || "Could not load leads.");
    } finally {
      setLoading(false);
    }
  }, [request]);

  useEffect(() => {
    loadLeads();
  }, [loadLeads]);

  const counts = useMemo(() => {
    const followups = leads.filter((lead) => lead.nextFollowUp);

    return {
      total: leads.length,
      new: leads.filter((lead) => lead.status === "NEW").length,
      today: followups.filter((lead) => isToday(lead.nextFollowUp)).length,
      overdue: followups.filter((lead) => isOverdue(lead.nextFollowUp)).length,
      converted: leads.filter((lead) => lead.status === "CONVERTED").length,
      qualified: leads.filter((lead) => lead.status === "QUALIFIED").length,
    };
  }, [leads]);

  const visibleLeads = useMemo(() => {
    const q = search.trim().toLowerCase();

    return leads.filter((lead) => {
      const matchesSearch =
        !q ||
        [
          lead.fullName,
          lead.phone,
          lead.email,
          lead.companyName,
          lead.source,
        ].some((value) => String(value || "").toLowerCase().includes(q));

      const matchesStatus =
        statusFilter === "ALL" || lead.status === statusFilter;

      const matchesView =
        view === "overview" ||
        view === "leads" ||
        (view === "new" && lead.status === "NEW") ||
        (view === "today" && isToday(lead.nextFollowUp)) ||
        (view === "overdue" && isOverdue(lead.nextFollowUp)) ||
        (view === "converted" && lead.status === "CONVERTED") ||
        (view === "qualified" && lead.status === "QUALIFIED");

      return matchesSearch && matchesStatus && matchesView;
    });
  }, [leads, search, statusFilter, view]);

  function openView(nextView) {
    setView(nextView);
    setStatusFilter("ALL");
    setSearch("");
    setNotice("");
    setError("");
  }

  async function saveLead(event) {
    event.preventDefault();
    setSaving(true);
    setError("");
    setNotice("");

    try {
      await request("/leads", {
        method: "POST",
        body: JSON.stringify({
          fullName: form.fullName.trim(),
          phone: form.phone.trim(),
          email: form.email.trim(),
          companyName: form.companyName,
          source: form.source,
          notes: form.notes.trim(),
          nextFollowUp: form.nextFollowUp || null,
        }),
      });

      setForm({
        fullName: "",
        phone: "",
        email: "",
        companyName: "",
        source: "MANUAL",
        notes: "",
        nextFollowUp: "",
      });

      setNotice("Lead saved successfully.");
      await loadLeads();
      setView("leads");
    } catch (e) {
      setError(e.message || "Could not save lead.");
    } finally {
      setSaving(false);
    }
  }

  async function updateStatus(lead, status) {
    setError("");
    setNotice("");

    try {
      await request(`/leads/${lead.id}`, {
        method: "PUT",
        body: JSON.stringify({
          status,
          notes: lead.notes || "",
          nextFollowUp: lead.nextFollowUp || null,
        }),
      });

      setNotice("Lead status updated.");
      await loadLeads();
    } catch (e) {
      setError(
        "Status update failed. Check that the backend PUT /api/leads/{id} accepts this request."
      );
    }
  }

  const titleMap = {
    overview: "Dashboard Overview",
    leads: "Lead Management",
    new: "New Leads",
    today: "Today's Follow-ups",
    overdue: "Overdue Follow-ups",
    converted: "Converted Leads",
    qualified: "Qualified Leads",
    add: "Add New Lead",
    employees: "Employee Management",
    organizations: "Organization Management",
  };

  return (
    <div className="crm-shell">
      <aside className="crm-sidebar">
        <div className="crm-brand">
          <div className="crm-brand-mark">P</div>
          <div>
            <strong>Potech CRM</strong>
            <span>Lead Management</span>
          </div>
        </div>

        <div className="crm-nav-label">WORKSPACE</div>
        <button className={view === "overview" ? "crm-nav active" : "crm-nav"} onClick={() => openView("overview")}>▦ <span>Overview</span></button>
        <button className={view === "leads" ? "crm-nav active" : "crm-nav"} onClick={() => openView("leads")}>☷ <span>All Leads</span></button>
        <button className={view === "today" ? "crm-nav active" : "crm-nav"} onClick={() => openView("today")}>◷ <span>Today's Follow-ups</span>{counts.today > 0 && <b>{counts.today}</b>}</button>
        <button className={view === "overdue" ? "crm-nav active" : "crm-nav"} onClick={() => openView("overdue")}>⚠ <span>Overdue Follow-ups</span>{counts.overdue > 0 && <b>{counts.overdue}</b>}</button>

        <div className="crm-nav-label">MANAGE</div>
        <button className={view === "add" ? "crm-nav active" : "crm-nav"} onClick={() => openView("add")}>＋ <span>Add Lead</span></button>
        <button className={view === "employees" ? "crm-nav active" : "crm-nav"} onClick={() => openView("employees")}>♙ <span>Employees</span></button>
        {isPortalAdmin && (
          <button className={view === "organizations" ? "crm-nav active" : "crm-nav"} onClick={() => openView("organizations")}>▤ <span>Organizations</span></button>
        )}

        <div className="crm-sidebar-bottom">
          <div className="crm-user-avatar">{String(user.name || user.email || "A").charAt(0).toUpperCase()}</div>
          <div className="crm-user-meta">
            <strong>{user.name || "CRM User"}</strong>
            <span>{role.replaceAll("_", " ") || "USER"}</span>
          </div>
          <button className="crm-logout" title="Logout" onClick={onLogout}>↗</button>
        </div>
      </aside>

      <main className="crm-main">
        <header className="crm-topbar">
          <div>
            <div className="crm-breadcrumb">Workspace / Potech CRM</div>
            <h1>{titleMap[view] || "Potech CRM"}</h1>
          </div>
          <div className="crm-top-actions">
            <span className="crm-date">{new Date().toLocaleDateString("en-IN", { weekday: "short", day: "2-digit", month: "short", year: "numeric" })}</span>
            <button className="crm-primary-btn" onClick={() => openView("add")}>＋ Add Lead</button>
          </div>
        </header>

        {error && <div className="crm-alert error">{error}</div>}
        {notice && <div className="crm-alert success">{notice}</div>}

        {(view === "overview" || view === "leads" || view === "new" || view === "today" || view === "overdue" || view === "converted" || view === "qualified") && (
          <>
            {view === "overview" && (
              <>
                <section className="crm-welcome">
                  <div>
                    <span className="crm-eyebrow">YOUR SALES WORKSPACE</span>
                    <h2>Good work starts with clear follow-ups.</h2>
                    <p>Track incoming enquiries, prioritize callbacks, and move leads towards conversion.</p>
                  </div>
                  <button className="crm-light-btn" onClick={() => openView("today")}>View today's tasks →</button>
                </section>

                <section className="crm-stats">
                  <button className="crm-stat" onClick={() => openView("leads")}>
                    <span className="crm-stat-icon blue">☷</span><span className="crm-stat-label">Total Leads</span><strong>{counts.total}</strong><small>View all leads →</small>
                  </button>
                  <button className="crm-stat" onClick={() => openView("new")}>
                    <span className="crm-stat-icon purple">✦</span><span className="crm-stat-label">New Leads</span><strong>{counts.new}</strong><small>Needs first contact →</small>
                  </button>
                  <button className="crm-stat" onClick={() => openView("today")}>
                    <span className="crm-stat-icon orange">◷</span><span className="crm-stat-label">Today's Follow-ups</span><strong>{counts.today}</strong><small>Open today's list →</small>
                  </button>
                  <button className="crm-stat" onClick={() => openView("overdue")}>
                    <span className="crm-stat-icon red">⚠</span><span className="crm-stat-label">Overdue Follow-ups</span><strong>{counts.overdue}</strong><small>Needs attention →</small>
                  </button>
                  <button className="crm-stat" onClick={() => openView("qualified")}>
                    <span className="crm-stat-icon teal">✓</span><span className="crm-stat-label">Qualified</span><strong>{counts.qualified}</strong><small>View qualified →</small>
                  </button>
                  <button className="crm-stat" onClick={() => openView("converted")}>
                    <span className="crm-stat-icon green">↗</span><span className="crm-stat-label">Converted</span><strong>{counts.converted}</strong><small>View conversions →</small>
                  </button>
                </section>
              </>
            )}

            <section className="crm-panel">
              <div className="crm-panel-heading">
                <div>
                  <h2>{view === "overview" ? "Recent Leads" : titleMap[view]}</h2>
                  <p>{visibleLeads.length} record(s) found</p>
                </div>
                <button className="crm-primary-btn" onClick={() => openView("add")}>＋ New Lead</button>
              </div>

              <div className="crm-filters">
                <input aria-label="Search leads" placeholder="Search name, mobile, email or service..." value={search} onChange={(e) => setSearch(e.target.value)} />
                <select aria-label="Filter by status" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                  <option value="ALL">All statuses</option>
                  {STATUS_OPTIONS.map((status) => <option key={status} value={status}>{status}</option>)}
                </select>
                <button className="crm-light-btn" onClick={loadLeads}>↻ Refresh</button>
              </div>

              {loading ? <div className="crm-empty">Loading leads…</div> : (
                <div className="crm-table-wrap">
                  <table className="crm-table">
                    <thead>
                      <tr><th>Customer</th><th>Mobile / Email</th><th>Service</th><th>Source</th><th>Status</th><th>Next Follow-up</th></tr>
                    </thead>
                    <tbody>
                      {visibleLeads.map((lead) => (
                        <tr key={lead.id}>
                          <td><strong>{lead.fullName || "Unnamed lead"}</strong><small>Lead #{lead.id}</small></td>
                          <td>{lead.phone || "—"}<small>{lead.email || ""}</small></td>
                          <td>{lead.companyName || "—"}</td>
                          <td>{String(lead.source || "MANUAL").replaceAll("_", " ")}</td>
                          <td>
                            <select className={`crm-status status-${String(lead.status || "NEW").toLowerCase()}`} value={lead.status || "NEW"} onChange={(e) => updateStatus(lead, e.target.value)}>
                              {STATUS_OPTIONS.map((status) => <option key={status} value={status}>{status}</option>)}
                            </select>
                          </td>
                          <td>{formatDate(lead.nextFollowUp)}</td>
                        </tr>
                      ))}
                      {!visibleLeads.length && <tr><td colSpan="6"><div className="crm-empty">No leads match this view. Try another filter or add a lead.</div></td></tr>}
                    </tbody>
                  </table>
                </div>
              )}
            </section>
          </>
        )}

        {view === "add" && (
          <section className="crm-panel crm-form-panel">
            <div className="crm-panel-heading">
              <div><h2>Create a new lead</h2><p>Enter customer details and schedule the next follow-up.</p></div>
            </div>
            <form className="crm-lead-form" onSubmit={saveLead}>
              <label>Customer name *<input required value={form.fullName} onChange={(e) => setForm({ ...form, fullName: e.target.value })} /></label>
              <label>Mobile number *<input required inputMode="tel" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></label>
              <label>Email address<input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></label>
              <label>Service *
                <select required value={form.companyName} onChange={(e) => setForm({ ...form, companyName: e.target.value })}>
                  <option value="">Select service</option>
                  {SERVICE_OPTIONS.map((service) => <option key={service} value={service}>{service}</option>)}
                </select>
              </label>
              <label>Lead source
                <select value={form.source} onChange={(e) => setForm({ ...form, source: e.target.value })}>
                  <option value="MANUAL">Manual</option><option value="FACEBOOK_AD">Facebook Ad</option><option value="WHATSAPP">WhatsApp</option><option value="WEBSITE">Website</option><option value="REFERRAL">Referral</option><option value="PHONE">Phone</option>
                </select>
              </label>
              <label>Next follow-up<input type="datetime-local" value={form.nextFollowUp} onChange={(e) => setForm({ ...form, nextFollowUp: e.target.value })} /></label>
              <label className="crm-full-width">Notes<textarea rows="4" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Customer requirement, callback details…" /></label>
              <div className="crm-form-actions crm-full-width">
                <button type="button" className="crm-light-btn" onClick={() => openView("leads")}>Cancel</button>
                <button type="submit" className="crm-primary-btn" disabled={saving}>{saving ? "Saving…" : "Save Lead"}</button>
              </div>
            </form>
          </section>
        )}

        {(view === "employees" || view === "organizations") && (
          <section className="crm-panel">
            <div className="crm-empty crm-feature-note">
              <h2>{view === "employees" ? "Employee Management" : "Organization Management"}</h2>
              <p>
                {view === "employees"
                  ? "The employee screen needs secured backend APIs for creating users, assigning roles, and assigning leads."
                  : "The organization screen needs secured Portal Admin APIs for creating and managing tenant organizations."}
              </p>
              <p>This page must be connected to the backend before records can be created. No fake records are shown.</p>
            </div>
          </section>
        )}
      </main>
    </div>
  );
}
